import {
  ClientEvent,
  type MediaProgress,
  type MemberSyncView,
  type RoomMemberView,
  type RoomQueueItemView,
  type RoomSnapshot,
  ServerEvent,
} from '@anisync/types';
import { useEffect, useMemo, useState } from 'react';
import { getSocket } from '../lib/socket';
import { useServerClockOffset } from './use-server-clock-offset';

/**
 * Stan pokoju złożony ze zdarzeń.
 *
 * Migawka przychodzi raz, przy wejściu, a potem stan jest **aktualizowany
 * przyrostowo**. Ponowne pobieranie całości po każdej zmianie byłoby prostsze
 * w kodzie, ale przy każdym takcie leadera przesyłałoby całą listę uczestników
 * i kolejkę — czyli setki bajtów co trzy sekundy zamiast dziesięciu.
 */

export interface ChatEntry {
  id: string;
  author: string;
  isGuest: boolean;
  content: string;
  type: 'message' | 'system' | 'reaction';
  createdAt: string;
}

/** Stan potrzebny do synchronizacji. Wydzielony, bo zmienia się najczęściej. */
export interface SyncState {
  time: number;
  /** Stempel SERWERA z chwili emisji. Podstawa ekstrapolacji u followera. */
  emittedAt: number | null;
  paused: boolean;
}

export interface RoomView {
  connected: boolean;
  error: string | null;
  snapshot: RoomSnapshot | null;
  members: RoomMemberView[];
  queue: RoomQueueItemView[];
  chat: ChatEntry[];
  current: RoomQueueItemView | null;
  sync: SyncState;
  leaderUserKey: string | null;
  /** Kto to „ja" — z migawki, bo serwer zna klucz, a przeglądarka nie. */
  myUserKey: string | null;
  offsetMs: number;
  /** Rośnie przy każdej zmianie materiału — wyzwala kontrolę bezpieczeństwa. */
  mediaEpoch: number;
  /** Sekundy do automatycznego przejścia; null gdy nic nie odlicza. */
  advanceIn: number | null;
  /** Reakcje z ostatnich chwil. Ulotne — nie zapisujemy ich nigdzie. */
  reactions: Array<{ id: number; displayName: string; emoji: string }>;
  /** Powód usunięcia z pokoju. Ustawiony raz i nieodwracalny w tej karcie. */
  kickedReason: string | null;
  /** Ostatni postęp pobierania bieżącego materiału; null, gdy nic nie przyszło. */
  progress: MediaProgress | null;
  /**
   * Zgodność uczestników z odtwarzaniem, po kluczu tożsamości.
   *
   * Liczona przez SERWER: tylko on zna wspólny czas odniesienia, a przeliczanie
   * u każdego z osobna dodawałoby do wyniku jego własne przesunięcie zegara.
   */
  memberSync: Map<string, MemberSyncView>;
  send: {
    update(time: number, paused: boolean): void;
    skipTo(uid: string): void;
    ended(): void;
    claimLeader(): void;
    chat(content: string): void;
    queueAdd(input: { titleId?: string; episodeNumber?: number; mediaFileId?: string }): void;
    /** Takt zgłoszeniowy: gdzie JA jestem w materiale. Wysyła każdy, nie tylko prowadzący. */
    heartbeat(time: number, paused: boolean): void;
    queueRemove(uid: string): void;
    mute(userKey: string, muted: boolean): void;
    kick(userKey: string): void;
    ban(userKey: string, reason?: string): void;
    reaction(emoji: string): void;
  };
}

export function useRoom(slug: string): RoomView {
  const socket = useMemo(() => getSocket(), []);
  const { offsetMs } = useServerClockOffset(socket);

  const [connected, setConnected] = useState(socket.connected);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [members, setMembers] = useState<RoomMemberView[]>([]);
  const [queue, setQueue] = useState<RoomQueueItemView[]>([]);
  const [chat, setChat] = useState<ChatEntry[]>([]);
  const [current, setCurrent] = useState<RoomQueueItemView | null>(null);
  const [sync, setSync] = useState<SyncState>({ time: 0, emittedAt: null, paused: true });
  const [leaderUserKey, setLeaderUserKey] = useState<string | null>(null);
  const [mediaEpoch, setMediaEpoch] = useState(0);
  const [advanceIn, setAdvanceIn] = useState<number | null>(null);
  const [reactions, setReactions] = useState<
    Array<{ id: number; displayName: string; emoji: string }>
  >([]);
  const [kickedReason, setKickedReason] = useState<string | null>(null);
  const [progress, setProgress] = useState<MediaProgress | null>(null);
  const [memberSync, setMemberSync] = useState<Map<string, MemberSyncView>>(new Map());

  /** Klucz własnej tożsamości — podaje go serwer w migawce. */
  const [myUserKey, setMyUserKey] = useState<string | null>(null);

  useEffect(() => {
    const onConnect = (): void => {
      setConnected(true);
      setError(null);
      // Wejście do pokoju powtarzamy po KAŻDYM połączeniu, nie tylko pierwszym:
      // po zerwaniu serwer usunął nas z pokoju razem ze starym gniazdem.
      socket.emit(ClientEvent.RoomJoin, { slug });
    };
    const onDisconnect = (): void => setConnected(false);

    const onState = (snap: RoomSnapshot): void => {
      setSnapshot(snap);
      setMembers(snap.members);
      setQueue(snap.queue);
      setLeaderUserKey(snap.leaderUserKey);
      setCurrent(snap.current?.queueItem ?? null);
      if (snap.current !== null) {
        setSync({
          time: snap.current.time,
          emittedAt: snap.current.emittedAt,
          paused: snap.current.paused,
        });
        setMediaEpoch((e) => e + 1);
      }
      // Własny klucz podaje serwer. Zgadywanie po liście uczestników wskazałoby
      // złą osobę, gdy dwoje wchodzi w tej samej chwili.
      setMyUserKey(snap.youUserKey);
    };

    const onUserList = (p: { members: RoomMemberView[] }): void => setMembers(p.members);
    const onQueue = (p: { queue: RoomQueueItemView[] }): void => setQueue(p.queue);
    const onLeader = (p: { leaderUserKey: string | null }): void =>
      setLeaderUserKey(p.leaderUserKey);

    const onMediaUpdate = (p: { time: number; paused: boolean; emittedAt: number }): void => {
      setSync({ time: p.time, emittedAt: p.emittedAt, paused: p.paused });
    };

    const onMediaChange = (p: {
      queueItem: RoomQueueItemView;
      time: number;
      paused: boolean;
      emittedAt: number | null;
    }): void => {
      setCurrent(p.queueItem);
      setSync({ time: p.time, emittedAt: p.emittedAt, paused: p.paused });
      setMediaEpoch((e) => e + 1);
      setAdvanceIn(null);
    };

    const onChat = (p: ChatEntry | { history: ChatEntry[] }): void => {
      if ('history' in p) {
        setChat(p.history);
        return;
      }
      // Ograniczamy długość w pamięci: pokój po kilku godzinach miałby tysiące
      // wiadomości w stanie Reacta i przerysowywałby je przy każdej nowej.
      setChat((prev) => [...prev, p].slice(-300));
    };

    // `message`, nie `reason`: koperta zdarzenia jest zadeklarowana w typach,
    // a poprzednia wersja czytała pole, którego serwer nie wysyłał — okno błędu
    // pojawiało się puste.
    const onRoomError = (p: { code: string; message: string }): void => setError(p.message);

    const onAdvance = (p: { remaining: number }): void => {
      // Zero znaczy „już przechodzimy" — chowamy licznik, bo zmiana materiału
      // i tak przyjdzie osobnym zdarzeniem.
      setAdvanceIn(p.remaining > 0 ? p.remaining : null);
    };

    const onReaction = (p: { displayName: string; emoji: string }): void => {
      const id = Date.now() + Math.random();
      setReactions((prev) => [...prev, { id, ...p }].slice(-12));
      // Reakcja jest sygnałem chwilowym: znika sama, żeby nie zbierać ich
      // w nieskończoność na ekranie ani w pamięci.
      setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== id)), 3000);
    };

    const onKick = (p: { reason: string }): void => setKickedReason(p.reason);

    /**
     * Postęp pobierania z serwera, jeden strumień dla całego pokoju.
     *
     * Dzięki temu dziesięć osób czekających na ten sam odcinek to jeden nadawca,
     * a nie dziesięć niezależnych odpytywań co sekundę.
     */
    const onProgress = (p: MediaProgress): void => setProgress(p);

    const onMemberSync = (p: { members: MemberSyncView[] }): void => {
      setMemberSync(new Map(p.members.map((m) => [m.userKey, m])));
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on(ServerEvent.RoomState, onState);
    socket.on(ServerEvent.UserList, onUserList);
    socket.on(ServerEvent.QueueState, onQueue);
    socket.on(ServerEvent.LeaderChange, onLeader);
    socket.on(ServerEvent.MediaUpdate, onMediaUpdate);
    socket.on(ServerEvent.MediaChange, onMediaChange);
    socket.on(ServerEvent.ChatMessage, onChat);
    socket.on(ServerEvent.RoomError, onRoomError);
    socket.on(ServerEvent.MediaAdvanceCountdown, onAdvance);
    socket.on(ServerEvent.ReactionEmit, onReaction);
    socket.on(ServerEvent.RoomKick, onKick);
    socket.on(ServerEvent.MediaProgress, onProgress);
    socket.on(ServerEvent.UserSync, onMemberSync);

    if (socket.connected) onConnect();
    else socket.connect();

    return () => {
      socket.emit(ClientEvent.RoomLeave);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off(ServerEvent.MediaProgress, onProgress);
      socket.off(ServerEvent.UserSync, onMemberSync);
      socket.off(ServerEvent.RoomState, onState);
      socket.off(ServerEvent.UserList, onUserList);
      socket.off(ServerEvent.QueueState, onQueue);
      socket.off(ServerEvent.LeaderChange, onLeader);
      socket.off(ServerEvent.MediaUpdate, onMediaUpdate);
      socket.off(ServerEvent.MediaChange, onMediaChange);
      socket.off(ServerEvent.ChatMessage, onChat);
      socket.off(ServerEvent.RoomError, onRoomError);
      socket.off(ServerEvent.MediaAdvanceCountdown, onAdvance);
      socket.off(ServerEvent.ReactionEmit, onReaction);
      socket.off(ServerEvent.RoomKick, onKick);
    };
  }, [socket, slug]);

  const send = useMemo(
    () => ({
      update: (time: number, paused: boolean) =>
        socket.emit(ClientEvent.MediaUpdate, { time, paused }),
      skipTo: (uid: string) => socket.emit(ClientEvent.MediaSkip, { uid }),
      ended: () => socket.emit(ClientEvent.MediaEnded),
      heartbeat: (time: number, paused: boolean) =>
        socket.emit(ClientEvent.MediaHeartbeat, { time, paused }),
      claimLeader: () => socket.emit(ClientEvent.LeaderClaim),
      chat: (content: string) => socket.emit(ClientEvent.ChatSend, { content }),
      queueAdd: (input: { titleId?: string; episodeNumber?: number; mediaFileId?: string }) =>
        socket.emit(ClientEvent.QueueAdd, input),
      queueRemove: (uid: string) => socket.emit(ClientEvent.QueueRemove, { uid }),
      mute: (userKey: string, muted: boolean) =>
        socket.emit(ClientEvent.UserMute, { userKey, muted }),
      kick: (userKey: string) => socket.emit(ClientEvent.UserKick, { userKey }),
      ban: (userKey: string, reason?: string) =>
        socket.emit(ClientEvent.UserBan, { userKey, ...(reason !== undefined ? { reason } : {}) }),
      reaction: (emoji: string) => socket.emit(ClientEvent.ReactionSend, { emoji }),
    }),
    [socket],
  );

  return {
    connected,
    error,
    snapshot,
    members,
    queue,
    chat,
    current,
    sync,
    leaderUserKey,
    myUserKey,
    offsetMs,
    mediaEpoch,
    advanceIn,
    reactions,
    kickedReason,
    progress,
    memberSync,
    send,
  };
}
